"use client";

import {
	AnimatePresence,
	motion,
	useAnimate,
	usePresence,
	useReducedMotion,
} from "motion/react";
import {
	type HTMLAttributes,
	type ReactNode,
	useEffect,
	useRef,
} from "react";
import { cn } from "../../../lib/cn";

/**
 * Exit choreography for {@link VanishListItem}.
 * - `destructive` — flash muted red, slide left (e.g. notification delete)
 * - `success` — flash green, slide right (e.g. completed / checked)
 * - `neutral` — brief scale + fade (no directional slide / color flash)
 */
export type VanishExitVariant = "destructive" | "success" | "neutral";

interface VanishExitConfig {
	/** Flash color applied to {@link VanishListItemProps.flashSelector} nodes. */
	flashColor: string | null;
	/** Horizontal exit offset in px (negative = left). */
	exitX: number;
	/** Hold after scale before sliding out. */
	holdMs: number;
}

const VANISH_EXIT_BY_VARIANT: Record<VanishExitVariant, VanishExitConfig> = {
	destructive: {
		flashColor: "rgb(252 165 165)", // red-300
		exitX: -24,
		holdMs: 750,
	},
	success: {
		flashColor: "rgb(110 231 183)", // emerald-300
		exitX: 24,
		holdMs: 750,
	},
	neutral: {
		flashColor: null,
		exitX: 0,
		holdMs: 200,
	},
};

export interface VanishListProps extends HTMLAttributes<HTMLElement> {
	children: ReactNode;
	/** Wrapper element. Default `div`. Use `ul` when children are `li`. */
	as?: "div" | "ul";
}

/**
 * Presence root for vanishing rows — wrap mapped {@link VanishListItem}s.
 * Prefer deep imports: `@repo/ui/components/custom/vanish-list`.
 */
export function VanishList({
	children,
	as = "div",
	className,
	...rest
}: VanishListProps) {
	const Comp = as;
	return (
		<Comp
			className={cn(as === "ul" ? undefined : "w-full", className)}
			{...rest}
			data-vanish-list=""
		>
			<AnimatePresence mode="popLayout">{children}</AnimatePresence>
		</Comp>
	);
}

export interface VanishListItemProps {
	children: ReactNode;
	className?: string;
	/**
	 * Exit motion preset. Notifications delete uses `destructive`.
	 * @default "destructive"
	 */
	exitVariant?: VanishExitVariant;
	/**
	 * Nodes matching this selector receive the flash color on exit.
	 * @default "[data-vanish-flash]"
	 */
	flashSelector?: string;
	/** Layout projection while siblings reflow. @default true */
	layout?: boolean;
	/** Render as list item when parent is `ul`. @default "div" */
	as?: "div" | "li";
}

/**
 * Single row with Moti `usePresence` exit: flash → scale → slide/fade.
 * Must be a direct child of {@link VanishList} / `AnimatePresence`.
 */
export function VanishListItem({
	children,
	className,
	exitVariant = "destructive",
	flashSelector = "[data-vanish-flash]",
	layout = true,
	as = "div",
}: VanishListItemProps) {
	const [isPresent, safeToRemove] = usePresence();
	const [scope, animate] = useAnimate();
	const reducedMotion = useReducedMotion();
	const safeToRemoveRef = useRef(safeToRemove);
	safeToRemoveRef.current = safeToRemove;

	useEffect(() => {
		if (isPresent) {
			return;
		}

		const config = VANISH_EXIT_BY_VARIANT[exitVariant];
		const node = scope.current;
		if (!node) {
			safeToRemoveRef.current?.();
			return;
		}

		if (reducedMotion) {
			safeToRemoveRef.current?.();
			return;
		}

		let cancelled = false;

		async function runExit() {
			try {
				if (config.flashColor) {
					const flashNodes = node.querySelectorAll(flashSelector);
					if (flashNodes.length > 0) {
						await animate(
							flashSelector,
							{ color: config.flashColor },
							{ ease: "easeIn", duration: 0.125 },
						);
					}
				}
				if (cancelled) {
					return;
				}
				await animate(
					node,
					{ scale: 1.025 },
					{ ease: "easeIn", duration: 0.125 },
				);
				if (cancelled) {
					return;
				}
				await animate(
					node,
					{
						opacity: 0,
						x: config.exitX,
					},
					{
						delay: config.holdMs / 1000,
						duration: 0.2,
						ease: "easeIn",
					},
				);
			} finally {
				if (!cancelled) {
					safeToRemoveRef.current?.();
				}
			}
		}

		void runExit();
		return () => {
			cancelled = true;
		};
	}, [animate, exitVariant, flashSelector, isPresent, reducedMotion]);

	const MotionTag = as === "li" ? motion.li : motion.div;

	return (
		<MotionTag
			ref={scope}
			layout={layout}
			className={cn("relative w-full", className)}
			data-vanish-list-item=""
			data-vanish-exit-variant={exitVariant}
		>
			{children}
		</MotionTag>
	);
}
